// base-react/components/AppMain.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ConfigProvider, App as AntApp, Result, theme } from 'antd';
import { createBrowserRouter, Outlet, RouterProvider, useLocation } from 'react-router-dom';
import { Config } from '../scripts/config';
import { apis, hooks, models } from '@vote4dance/judging-sdk';
import { darkBodyColor, lightBodyColor } from '../styles/colors';
import { darkTheme, lightTheme } from '../styles/theme';
import { Localization, _T } from '../src/Localization';
import svSE from 'antd/locale/sv_SE';
import { UserSettingsProvider, useUserSettings, } from '../hooks/Storage';
import { getFeatureFlag, setFeatureFlags } from '../hooks/Features';
import { DocumentTitle } from './DocumentTitle';
import { ErrorElement } from './ErrorElement';
import { NotFound } from './NotFound';
import { LoginForm } from './LoginForm';
import { RegisterForm } from './RegisterForm';
var Api = apis.Api;
var useSession = hooks.sessions.useSession;
var NativeSource = models.server.NativeSource;
var UserSource = models.users.UserSource;
const { SessionProvider } = hooks.sessions;
/**
 * Top-level wrapper per frontend (manager-react, admin-react, etc.)
 * Routes are passed in and wired to React Router here.
 */
const App = ({ routes, theme }) => {
    var _a, _b;
    const basename = ((_b = (_a = Config.get('target')) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.replace(/[/]+/g, '')) || '';
    return (React.createElement(SessionProvider, null,
        React.createElement(AppWithRouter, { basename: basename, routes: routes, themeOverride: theme })));
};
/**
 * Handles:
 * - language loading (per user)
 * - router creation (once, via ref)
 * - top-level error element
 *
 * Settings/theme are handled *inside* BaseAppLayout so they don’t affect the router.
 */
const AppWithRouter = ({ basename, routes, themeOverride, }) => {
    const sessionState = useSession();
    // Load user language after login
    useEffect(() => {
        var _a;
        if ((sessionState === null || sessionState === void 0 ? void 0 : sessionState.isLoggedIn) && ((_a = sessionState.sessionInfo) === null || _a === void 0 ? void 0 : _a.user_id)) {
            Api.read(UserSource(sessionState.sessionInfo.user_id).url).then(userData => {
                if (userData &&
                    userData[0].user_language &&
                    userData[0].user_language !== '') {
                    Localization.changeLanguage(userData[0].user_language);
                }
            });
        }
    }, [sessionState === null || sessionState === void 0 ? void 0 : sessionState.isLoggedIn]);
    // Route config, wrapped in BaseAppLayout (which provides settings + theme)
    const enhancedRoutes = useMemo(() => [
        {
            element: React.createElement(BaseAppLayout, { themeOverride: themeOverride }),
            errorElement: React.createElement(ErrorElement, null),
            children: [
                {
                    path: 'login',
                    element: React.createElement(LoginForm, null),
                    handle: { titleKey: 'login.title' },
                },
                {
                    path: 'register',
                    element: React.createElement(RegisterForm, null),
                    handle: { titleKey: 'register.title' },
                },
                ...routes,
                {
                    path: '*',
                    element: React.createElement(NotFound, null),
                    handle: { titleKey: 'common.404' },
                },
            ],
        },
    ], [routes, themeOverride]);
    // Create router only once so settings/theme changes do NOT recreate it.
    const routerRef = useRef(null);
    if (!routerRef.current) {
        routerRef.current = createBrowserRouter(enhancedRoutes, {
            basename: `/${basename}`,
        });
    }
    return routerRef.current ? (React.createElement(RouterProvider, { router: routerRef.current, future: {
            v7_startTransition: true,
        } })) : null;
};
/**
 * Wraps everything visible in:
 * - UserSettingsProvider (settings + updateSettings)
 * - ConfigProvider (theme based on settings.theme)
 * - AntApp
 *
 * Because this is *inside* the router, calling updateSettings (e.g. from
 * ExternalAPI or theme switch) only re-renders this subtree – the router,
 * URL and modal state are preserved.
 */
const BaseAppLayout = ({ themeOverride }) => (React.createElement(UserSettingsProvider, null,
    React.createElement(BaseAppLayoutInner, { themeOverride: themeOverride })));
const BaseAppLayoutInner = ({ themeOverride }) => {
    const { settings } = useUserSettings();
    const { token } = theme.useToken();
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [nativeSettings] = hooks.streams.Stream(NativeSource);
    // Body background from theme
    useEffect(() => {
        const mode = settings.theme || 'dark';
        document.body.style.background =
            mode === 'light' ? lightBodyColor : darkBodyColor;
    }, [settings.theme]);
    useEffect(() => {
        if (!nativeSettings) {
            return;
        }
        setFeatureFlags(nativeSettings);
        setMaintenanceMode(getFeatureFlag(nativeSettings, 'maintenance', false));
    }, [nativeSettings]);
    const themeConfig = themeOverride ||
        ((settings.theme || 'dark') === 'light' ? lightTheme : darkTheme);
    return (React.createElement(ConfigProvider, { theme: themeConfig, locale: Localization.getLanguage() === 'sv' ? svSE : undefined },
        React.createElement(AntApp, null,
            React.createElement(DocumentTitle, null),
            React.createElement(AnalyticsPageView, null),
            maintenanceMode ? (React.createElement("div", { style: {
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: token.paddingXL,
                } },
                React.createElement(Result, { status: "error", title: _T('maintenance.title'), subTitle: _T('maintenance.description') }))) : (React.createElement(Outlet, null)))));
};
const AnalyticsPageView = () => {
    const location = useLocation();
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            if (typeof window.gtag !== 'function') {
                return;
            }
            const pagePath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            window.gtag('event', 'page_view', {
                page_path: pagePath,
                page_location: window.location.href,
                page_title: document.title,
            });
        }, 0);
        return () => window.clearTimeout(timeoutId);
    }, [location.hash, location.pathname, location.search]);
    return null;
};
export default App;
